<template>
  <div class="modal fade" id="securityEventModal" tabindex="-1" role="dialog">
    <div class="modal-dialog modal-xl modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-header bg-success">
          <h4 class="modal-title text-white">
            <i class="mdi mdi-shield-check me-2"></i>
            Security Event Details
          </h4>
          <button type="button" class="btn-close btn-close-white" data-bs-dismiss="modal"></button>
        </div>

        <div class="modal-body">
          <!-- Event Summary -->
          <div class="row mb-4">
            <div class="col-12">
              <div class="card border border-success">
                <div class="card-header bg-light">
                  <div class="d-flex justify-content-between align-items-center">
                    <h5 class="mb-0">Event Summary</h5>
                    <span class="badge fs-6 bg-success">
                      CLEAN - NO THREATS
                    </span>
                  </div>
                </div>
                <div class="card-body">
                  <div class="row">
                    <div class="col-md-6">
                      <table class="table table-sm table-borderless">
                        <tr>
                          <td width="30%"><strong>Event ID:</strong></td>
                          <td>{{ eventDetail.id }}</td>
                        </tr>
                        <tr>
                          <td><strong>Title:</strong></td>
                          <td>{{ eventDetail.title }}</td>
                        </tr>
                        <tr>
                          <td><strong>Type:</strong></td>
                          <td>
                            <span class="badge bg-info">{{ formatEventType(eventDetail.type) }}</span>
                          </td>
                        </tr>
                        <tr>
                          <td><strong>Source System:</strong></td>
                          <td>{{ eventDetail.source }}</td>
                        </tr>
                        <tr>
                          <td><strong>Detection Time:</strong></td>
                          <td>{{ eventDetail.timestamp }}</td>
                        </tr>
                      </table>
                    </div>
                    <div class="col-md-6">
                      <table class="table table-sm table-borderless">
                        <tr>
                          <td width="30%"><strong>Status:</strong></td>
                          <td>
                            <span class="badge bg-success">
                              <i class="mdi mdi-check-circle me-1"></i>
                              {{ eventDetail.status || 'COMPLETED' }}
                            </span>
                          </td>
                        </tr>
                        <tr v-if="eventDetail.ip">
                          <td><strong>Source IP:</strong></td>
                          <td>{{ eventDetail.ip }}</td>
                        </tr>
                        <tr>
                          <td><strong>Threat Level:</strong></td>
                          <td>
                            <span class="badge bg-success">NONE</span>
                          </td>
                        </tr>
                        <tr>
                          <td><strong>Action Required:</strong></td>
                          <td>
                            <span class="text-success">
                              <i class="mdi mdi-check-circle me-1"></i>
                              No action needed
                            </span>
                          </td>
                        </tr>
                        <tr>
                          <td><strong>Risk Assessment:</strong></td>
                          <td>
                            <div class="progress" style="height: 20px;">
                              <div class="progress-bar bg-success" role="progressbar"
                                   style="width: 100%">
                                100% Secure
                              </div>
                            </div>
                          </td>
                        </tr>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Event Description -->
          <div class="row mb-4">
            <div class="col-12">
              <div class="card">
                <div class="card-header bg-light">
                  <h5 class="mb-0">Event Description</h5>
                </div>
                <div class="card-body">
                  <p>{{ eventDetail.description }}</p>

                  <div class="alert alert-success mt-3">
                    <i class="mdi mdi-shield-check me-2"></i>
                    <strong>Security Assessment:</strong> This event represents normal system operation with no security concerns.
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Technical Details -->
          <div class="row mb-4">
            <div class="col-md-6">
              <div class="card">
                <div class="card-header bg-light">
                  <h6 class="mb-0">
                    <i class="mdi mdi-information-outline me-2"></i>
                    Technical Information
                  </h6>
                </div>
                <div class="card-body">
                  <div class="technical-details">
                    <div class="detail-item mb-2">
                      <strong>Protocol:</strong>
                      <span class="text-muted">HTTPS/TLS 1.3</span>
                    </div>
                    <div class="detail-item mb-2">
                      <strong>Authentication:</strong>
                      <span class="badge bg-success">Verified</span>
                    </div>
                    <div class="detail-item mb-2">
                      <strong>Encryption:</strong>
                      <span class="badge bg-success">AES-256</span>
                    </div>
                    <div class="detail-item mb-2">
                      <strong>Firewall Status:</strong>
                      <span class="badge bg-success">Protected</span>
                    </div>
                    <div class="detail-item">
                      <strong>Response Time:</strong>
                      <span class="text-success">{{ responseTime }}ms</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="col-md-6">
              <div class="card">
                <div class="card-header bg-light">
                  <h6 class="mb-0">
                    <i class="mdi mdi-chart-line me-2"></i>
                    Event Metrics
                  </h6>
                </div>
                <div class="card-body">
                  <div class="metrics-summary">
                    <div class="d-flex justify-content-between mb-3">
                      <span>Security Score:</span>
                      <strong class="text-success">100/100</strong>
                    </div>
                    <div class="d-flex justify-content-between mb-3">
                      <span>Compliance:</span>
                      <strong class="text-success">Passed</strong>
                    </div>
                    <div class="d-flex justify-content-between mb-3">
                      <span>Validation:</span>
                      <strong class="text-success">Complete</strong>
                    </div>
                    <div class="d-flex justify-content-between">
                      <span>Integrity Check:</span>
                      <strong class="text-success">Verified</strong>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Related Events -->
          <div class="row mb-4">
            <div class="col-12">
              <div class="card">
                <div class="card-header bg-light">
                  <h6 class="mb-0">
                    <i class="mdi mdi-link-variant me-2"></i>
                    Related Events (All Clean)
                  </h6>
                </div>
                <div class="card-body">
                  <div class="table-responsive">
                    <table class="table table-sm table-hover">
                      <thead>
                      <tr>
                        <th>Time</th>
                        <th>Event Type</th>
                        <th>Description</th>
                        <th>Status</th>
                      </tr>
                      </thead>
                      <tbody>
                      <tr v-for="(event, index) in relatedEvents" :key="index">
                        <td>{{ event.time }}</td>
                        <td>
                          <span class="badge bg-info">{{ event.type }}</span>
                        </td>
                        <td>{{ event.description }}</td>
                        <td>
                          <span class="badge bg-success">Clean</span>
                        </td>
                      </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Actions Taken -->
          <div class="row">
            <div class="col-12">
              <div class="card">
                <div class="card-header bg-light">
                  <h6 class="mb-0">
                    <i class="mdi mdi-check-all me-2"></i>
                    Automated Actions
                  </h6>
                </div>
                <div class="card-body">
                  <div class="timeline">
                    <div class="timeline-item" v-for="(action, index) in automatedActions" :key="index">
                      <div class="timeline-marker bg-success"></div>
                      <div class="timeline-content">
                        <div class="d-flex justify-content-between align-items-center mb-1">
                          <strong>{{ action.action }}</strong>
                          <small class="text-muted">{{ action.time }}</small>
                        </div>
                        <p class="mb-0 text-muted">{{ action.description }}</p>
                        <span class="badge bg-success mt-1">
                          <i class="mdi mdi-check me-1"></i>
                          {{ action.result }}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <div class="d-flex justify-content-between w-100">
            <div>
              <span class="text-success">
                <i class="mdi mdi-shield-check me-1"></i>
                No security threats detected
              </span>
            </div>
            <div>
              <button @click="exportEventReport" class="btn btn-outline-primary me-2">
                <i class="mdi mdi-file-export me-1"></i>
                Export Report
              </button>
              <button @click="acknowledgeEvent" class="btn btn-success me-2">
                <i class="mdi mdi-check me-1"></i>
                Acknowledge
              </button>
              <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
export default {
  name: 'SecurityEventModal',
  props: {
    eventDetail: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      responseTime: 125,
      relatedEvents: [
        {
          time: '14:30:00',
          type: 'AUTH_SUCCESS',
          description: 'User authentication successful',
          status: 'CLEAN'
        },
        {
          time: '14:25:00',
          type: 'SYSTEM_CHECK',
          description: 'System health check passed',
          status: 'CLEAN'
        },
        {
          time: '14:20:00',
          type: 'BACKUP_COMPLETE',
          description: 'Backup completed successfully',
          status: 'CLEAN'
        }
      ],
      automatedActions: [
        {
          action: 'Security Scan Initiated',
          time: '2 minutes ago',
          description: 'Automated security scan performed on the event',
          result: 'Passed'
        },
        {
          action: 'Event Logged',
          time: '1 minute ago',
          description: 'Event recorded in security audit log',
          result: 'Completed'
        },
        {
          action: 'Notification Sent',
          time: 'Just now',
          description: 'Admin notified of clean security status',
          result: 'Delivered'
        }
      ]
    }
  },
  methods: {
    formatEventType(type) {
      if (!type) return 'SYSTEM';
      return type.replace(/_/g, ' ');
    },

    acknowledgeEvent() {
      this.$emit('event-acknowledged', this.eventDetail.id);

      // Close modal
      const modalElement = document.getElementById('securityEventModal');
      if (modalElement) {
        const modal = bootstrap.Modal.getInstance(modalElement);
        if (modal) {
          modal.hide();
        }
      }
    },

    exportEventReport() {
      // Generate and download report
      const report = {
        event: this.eventDetail,
        assessment: 'No security threats detected',
        timestamp: new Date().toISOString()
      };

      const blob = new Blob([JSON.stringify(report, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `security-event-${this.eventDetail.id}-report.json`;
      a.click();
      URL.revokeObjectURL(url);
    }
  }
}
</script>

<style scoped>
.timeline {
  position: relative;
  padding-left: 30px;
}

.timeline::before {
  content: '';
  position: absolute;
  left: 10px;
  top: 0;
  bottom: 0;
  width: 2px;
  background: #e9ecef;
}

.timeline-item {
  position: relative;
  margin-bottom: 20px;
}

.timeline-marker {
  position: absolute;
  left: -24px;
  top: 5px;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  border: 2px solid #fff;
  box-shadow: 0 0 0 1px #e9ecef;
}

.timeline-content {
  background: #f8f9fa;
  padding: 10px 15px;
  border-radius: 5px;
}

.technical-details .detail-item {
  padding: 5px 0;
  border-bottom: 1px solid #f1f1f1;
}

.technical-details .detail-item:last-child {
  border-bottom: none;
}

.modal-header.bg-success {
  background: linear-gradient(135deg, #28a745 0%, #20c997 100%) !important;
}
</style>